import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import {
    Box,
    TextField,
    IconButton,
    Grid,
    Button,
    InputAdornment,
    Typography,
    FormControl,
    Select,
    MenuItem,
    Card,
    CardContent,
    Avatar,
    Chip,
    Tabs,
    Tab,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Divider,
    CircularProgress,
    FormHelperText,
    Alert,
    Tooltip,
} from "@mui/material";

import { Search, FilterAlt as FilterAltIcon, Refresh as RefreshIcon, Close as CloseIcon, Payment as PaymentIcon, Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, CalendarToday as CalendarIcon, Download as DownloadIcon, ShoppingCart as ShoppingCartIcon, CheckCircle as CheckCircleIcon } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Color from "../../components/Color";
import { getPayments, exportPayments } from "../../service/paymentService";
import { getAllBranchLists } from "../../service/branchService";
import { getSidebarCounts } from "../../service/dashboardService";
import { useSnackbar } from "../../App";
import { getOrderById, updateOrder } from "../../service/orderService";
import { format, addDays, subDays } from "date-fns";
import CountUp from "react-countup";

const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    const dateObj = new Date(dateStr);

    // Check if it's a valid date
    if (isNaN(dateObj.getTime())) return "-";

    const formattedDate = dateObj.toLocaleDateString("en-GB", {
        day: "2-digit", month: "2-digit", year: "numeric"
    });

    // If date string contains 00:00:00, we assume no specific time was provided
    if (typeof dateStr === 'string' && dateStr.includes("00:00:00")) {
        return `${formattedDate} -`;
    }

    const formattedTime = dateObj.toLocaleTimeString("en-GB", {
        hour: "2-digit", minute: "2-digit", hour12: true
    }).toUpperCase();

    return `${formattedDate} ${formattedTime}`;
};

const generateLocalCSV = (orders) => {
    let csv = "Order Number,Cashier Name,Customer,Branch,Total Amount,Pending Amount,Status,Payment Date & Time\n";

    orders.forEach(order => {
        const paid = parseFloat(order.paid_amount || 0);
        const total = parseFloat(order.total_amount || 0);
        const pending = Math.max(0, total - paid);
        
        let status = paid >= total ? "Full" : "Advance";
        if (status === "Advance" && total > 0 && total === pending) {
            status = "Not Paid";
        }

        let paymentDate = "-";
        let cashierName = order.createdBy?.name || "N/A";
        if (order.payments && Array.isArray(order.payments) && order.payments.length > 0) {
            const sortedPayments = [...order.payments].sort((a, b) => new Date(b.payment_date) - new Date(a.payment_date));
            const latestPayment = sortedPayments[0];
            if (latestPayment) {
                if (latestPayment.payment_date) {
                    paymentDate = formatDateTime(latestPayment.payment_date);
                }
                if (latestPayment.cashier_name) {
                    cashierName = latestPayment.cashier_name;
                }
            }
        }

        csv += `"${order.order_number}","${cashierName}","${order.customer?.name || "N/A"}","${order.branch?.name || "N/A"}","${total.toFixed(2)}","${pending.toFixed(2)}","${status}","${paymentDate}"\n`;
    });
    return csv;
};

const Payments = () => {
    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [rowCount, setRowCount] = useState(0);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [search, setSearch] = useState("");
    const [branches, setBranches] = useState([]);
    const [tabValue, setTabValue] = useState(0);
    const userRole = localStorage.getItem("role");
    const managerBranchId = localStorage.getItem("branch_id");
    const isManager = userRole === "2";

    const [filters, setFilters] = useState({
        branch_id: isManager ? managerBranchId : "",
        from_date: null,
        to_date: null,
        payment_status: "not_paid",
    });

    const [tabCounts, setTabCounts] = useState({ advance: 0, full: 0, not_paid: 0 });

    // Payment Modal states
    const [payModalOpen, setPayModalOpen] = useState(false);
    const [paymentLoading, setPaymentLoading] = useState(false);
    const [submittingPayment, setSubmittingPayment] = useState(false);
    const [orderData, setOrderData] = useState(null);
    const [paymentErrors, setPaymentErrors] = useState({});

    // Modal Editable Payments state
    const [modalPayments, setModalPayments] = useState([]);
    const [originalPayments, setOriginalPayments] = useState([]);
    const [showAddPaymentFields, setShowAddPaymentFields] = useState(false);
    const [editingPaymentIndex, setEditingPaymentIndex] = useState(null);
    const [newPayment, setNewPayment] = useState({ amount: "", payment_method: "", payment_date: new Date(), cashier_name: "" });

    const handleOpenPayModal = async (orderId) => {
        setPayModalOpen(true);
        setPaymentLoading(true);
        setPaymentErrors({});
        setNewPayment({ amount: "", payment_method: "", payment_date: new Date(), cashier_name: "" });
        setShowAddPaymentFields(false);
        setEditingPaymentIndex(null);
        setOrderData(null);
        try {
            const res = await getOrderById(orderId);
            const order = res?.data?.data;
            if (order) {
                setOrderData(order);
                const payments = (order.payments || []).map((p) => ({
                    id: p.id,
                    amount: parseFloat(p.amount || 0).toFixed(2),
                    payment_method: p.payment_method || "",
                    payment_date: p.payment_date ? new Date(p.payment_date) : null,
                    cashier_name: p.cashier_name || "",
                    status: p.status || "completed"
                }));
                setModalPayments(payments);
                setOriginalPayments(payments.map(p => ({
                    ...p,
                    payment_date: p.payment_date ? new Date(p.payment_date.getTime()) : null
                })));
            } else {
                showSnackbar("Order details not found!", "error");
                setPayModalOpen(false);
            }
        } catch (error) {
            console.error("Fetch order details error:", error);
            showSnackbar("Failed to fetch order details!", "error");
            setPayModalOpen(false);
        } finally {
            setPaymentLoading(false);
        }
    };

    const getModalTotals = () => {
        if (!orderData) return { total: 0, paid: 0, pendingApproval: 0, remainingToCollect: 0, pending: 0 };
        const total = parseFloat(orderData.total_amount || 0);
        const paid = modalPayments.reduce((sum, p) => {
            if (p.status === 'pending_approval' || p.status === 'rejected') return sum;
            return sum + (parseFloat(p.amount) || 0);
        }, 0);
        const pendingApproval = modalPayments.reduce((sum, p) => {
            if (p.status === 'pending_approval') return sum + (parseFloat(p.amount) || 0);
            return sum;
        }, 0);
        const remainingToCollect = Math.max(0, total - (paid + pendingApproval));
        const pending = Math.max(0, total - paid);
        return { total, paid, pendingApproval, remainingToCollect, pending };
    };

    const handleAddPayment = () => {
        const { total } = getModalTotals();
        if (!newPayment.payment_method) {
            setPaymentErrors(prev => ({ ...prev, payment_method: "Payment method is required" }));
            return;
        }
        if (!newPayment.amount) {
            setPaymentErrors(prev => ({ ...prev, amount: "Amount is required" }));
            return;
        }
        if (!newPayment.cashier_name || !newPayment.cashier_name.trim()) {
            setPaymentErrors(prev => ({ ...prev, cashier_name: "Cashier name is required" }));
            return;
        }
        const numericAmount = parseFloat(newPayment.amount);
        if (isNaN(numericAmount) || numericAmount <= 0) {
            setPaymentErrors(prev => ({ ...prev, amount: "Amount must be greater than 0" }));
            return;
        }

        const { remainingToCollect, pendingApproval } = getModalTotals();
        if (numericAmount > remainingToCollect) {
            if (pendingApproval > 0) {
                setPaymentErrors(prev => ({
                    ...prev,
                    amount: `Max allowed payment is ₹${remainingToCollect.toFixed(2)} (₹${pendingApproval.toFixed(2)} is pending admin approval)`
                }));
            } else {
                setPaymentErrors(prev => ({ ...prev, amount: `Total payments cannot exceed total amount (₹${total.toFixed(2)})` }));
            }
            return;
        }

        setModalPayments(prev => [
            ...prev,
            {
                amount: numericAmount.toFixed(2),
                payment_method: newPayment.payment_method,
                payment_date: newPayment.payment_date || new Date(),
                cashier_name: newPayment.cashier_name || ""
            }
        ]);

        setNewPayment({ amount: "", payment_method: "", payment_date: new Date(), cashier_name: "" });
        setShowAddPaymentFields(false);
        setPaymentErrors({});
    };

    const handleEditPayment = (index) => {
        setEditingPaymentIndex(index);
        setShowAddPaymentFields(true);
        setNewPayment({
            amount: parseFloat(modalPayments[index].amount).toString(),
            payment_method: modalPayments[index].payment_method,
            payment_date: modalPayments[index].payment_date ? new Date(modalPayments[index].payment_date) : new Date(),
            cashier_name: modalPayments[index].cashier_name || ""
        });
        setPaymentErrors({});
    };

    const handleSaveEditedPayment = () => {
        const { total } = getModalTotals();
        if (!newPayment.payment_method) {
            setPaymentErrors(prev => ({ ...prev, payment_method: "Payment method is required" }));
            return;
        }
        if (!newPayment.amount) {
            setPaymentErrors(prev => ({ ...prev, amount: "Amount is required" }));
            return;
        }
        if (!newPayment.cashier_name || !newPayment.cashier_name.trim()) {
            setPaymentErrors(prev => ({ ...prev, cashier_name: "Cashier name is required" }));
            return;
        }
        const numericAmount = parseFloat(newPayment.amount);
        if (isNaN(numericAmount) || numericAmount <= 0) {
            setPaymentErrors(prev => ({ ...prev, amount: "Amount must be greater than 0" }));
            return;
        }

        const otherPaymentsPaid = modalPayments.reduce((sum, p, idx) => {
            if (idx === editingPaymentIndex) return sum;
            return sum + (parseFloat(p.amount) || 0);
        }, 0);

        if (otherPaymentsPaid + numericAmount > total) {
            setPaymentErrors(prev => ({ ...prev, amount: `Total payments cannot exceed total amount (₹${total.toFixed(2)})` }));
            return;
        }

        const updated = [...modalPayments];
        updated[editingPaymentIndex] = {
            amount: numericAmount.toFixed(2),
            payment_method: newPayment.payment_method,
            payment_date: newPayment.payment_date || new Date(),
            cashier_name: newPayment.cashier_name || ""
        };

        setModalPayments(updated);
        setNewPayment({ amount: "", payment_method: "", payment_date: new Date(), cashier_name: "" });
        setEditingPaymentIndex(null);
        setShowAddPaymentFields(false);
        setPaymentErrors({});
    };

    const handleRemovePayment = (index) => {
        setModalPayments(prev => prev.filter((_, idx) => idx !== index));
        setPaymentErrors({});
    };

    const handleCancelEditPayment = () => {
        setNewPayment({ amount: "", payment_method: "", payment_date: new Date(), cashier_name: "" });
        setEditingPaymentIndex(null);
        setShowAddPaymentFields(false);
        setPaymentErrors({});
    };

    const handleSubmitPayment = async () => {
        if (!orderData) return;

        if (showAddPaymentFields) {
            showSnackbar("Please save or cancel the active payment form before submitting", "warning");
            return;
        }

        const isIdentical = modalPayments.length === originalPayments.length &&
            modalPayments.every((p, idx) => {
                const orig = originalPayments[idx];
                const pDate = p.payment_date ? new Date(p.payment_date).getTime() : null;
                const origDate = orig.payment_date ? new Date(orig.payment_date).getTime() : null;
                return p.amount === orig.amount &&
                       p.payment_method === orig.payment_method &&
                       p.cashier_name === orig.cashier_name &&
                       pDate === origDate;
            });

        if (isIdentical) {
            showSnackbar("Please add a payment before submitting.", "warning");
            return;
        }

        setSubmittingPayment(true);
        try {
            const formatDateForBackend = (date) => {
                if (!date) return null;
                const parsedDate = date instanceof Date ? date : new Date(date);
                if (isNaN(parsedDate)) return null;
                return format(parsedDate, 'yyyy-MM-dd HH:mm:ss');
            };

            const paymentsPayload = modalPayments.map(p => ({
                ...(p.id ? { id: p.id } : {}),
                amount: parseFloat(p.amount) || 0,
                payment_method: p.payment_method,
                payment_date: formatDateForBackend(p.payment_date),
                cashier_name: p.cashier_name || ""
            }));

            const { pending } = getModalTotals();

            const payload = {
                order_number: orderData.order_number,
                customer_id: Number(orderData.customer_id || orderData.customer?.id),
                order_type: orderData.order_type,
                branch_id: Number(orderData.branch_id || orderData.branch?.id),
                status: orderData.status,
                order_date: orderData.order_date ? formatDateForBackend(new Date(orderData.order_date)) : null,
                delivery_date: orderData.delivery_date ? formatDateForBackend(new Date(orderData.delivery_date)) : null,
                delivery_address: orderData.delivery_address,
                payments: paymentsPayload,
                pending: parseFloat(pending.toFixed(2)),
                packing_charges: parseFloat(orderData.packing_charges) || 0,
                utensils_charge: parseFloat(orderData.utensils_charge) || 0,
                salesman_id: orderData.salesman_id || orderData.salesman?.id || undefined,
                delivery_type: orderData.delivery_type,
                notes: orderData.notes,
                branch_code: orderData.branch_code || orderData.branch?.code,
                discount_type: orderData.discount_type,
                discount_percentage: parseFloat(orderData.discount_percentage) || 0,
                discount_fixed: parseFloat(orderData.discount_fixed) || 0,
                tax_amount: parseFloat(orderData.tax_amount) || 0,
                quotation_tax_percentage: parseFloat(orderData.quotation_tax_percentage) || 0,
                carton_box_count: parseInt(orderData.carton_box_count) || 0,
                customer: {
                    name: orderData.customer?.name,
                    email: orderData.customer?.email,
                    phone: orderData.customer?.phone,
                    address: orderData.customer?.address,
                    gst_number: orderData.customer?.gst_number || "",
                    contact_type: orderData.customer?.contact_type,
                },
                items: (orderData.items || []).map((item) => ({
                    product_id: Number(item.product?.id || item.product_id),
                    quantity: Number(item.quantity),
                    price: parseFloat(item.price || item.product?.base_price || 0),
                })),
            };

            const res = await updateOrder(orderData.id, payload);
            if (res?.data?.status === "success" || res?.data?.data) {
                showSnackbar("Order payments updated successfully!", "success");
                setPayModalOpen(false);
                fetchPayments();
                fetchTabCounts();
            } else {
                showSnackbar("Failed to update payments. Invalid response.", "error");
            }
        } catch (error) {
            console.error("Submit payments error:", error);
            showSnackbar(error?.response?.data?.message || "Failed to update payments!", "error");
        } finally {
            setSubmittingPayment(false);
        }
    };

    const fetchTabCounts = async () => {
        try {
            const res = await getSidebarCounts();
            if (res?.data?.status === "success") {
                setTabCounts({
                    advance: res.data.data.payments?.advance_count || 0,
                    full: res.data.data.payments?.full_count || 0,
                    not_paid: res.data.data.payments?.not_paid_count || 0
                });
            }
        } catch (error) {
            console.error("Fetch tab counts error:", error);
        }
    };



    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const statusColors = {
        advance: { color: "#ffffff", backgroundColor: "#ff9800" },
        partial: { color: "#ffffff", backgroundColor: "#2196f3" },
        paid: { color: "#ffffff", backgroundColor: "#4caf50" },
        full: { color: "#ffffff", backgroundColor: "#4caf50" },
        unpaid: { color: "#ffffff", backgroundColor: "#f44336" },
        "not paid": { color: "#ffffff", backgroundColor: "#f44336" },
    };

    const paymentMethods = [
        { label: "Cash", value: "cash" },
        { label: "UPI", value: "upi" },
        { label: "Card", value: "card" },
        { label: "Bank Transfer", value: "bank_transfer" },
    ];


    const formatDate = (date) => {
        if (!date) return null;
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const fetchBranches = async () => {
        try {
            const res = await getAllBranchLists();
            if (res?.data?.data && Array.isArray(res.data.data)) {
                setBranches(res.data.data);
            } else if (Array.isArray(res?.data)) {
                setBranches(res.data);
            }
        } catch (error) {
            console.error("Fetch branches error:", error);
        }
    };


    const fetchPayments = useCallback(async () => {
        setLoading(true);
        try {
            const currentStatus = tabValue === 0 ? "not_paid" : tabValue === 1 ? "advance" : "full";
            const isNotPaid = tabValue === 0;
            const hasDateFilter = filters.from_date || filters.to_date;

            const params = {
                page: (isNotPaid && hasDateFilter) ? 1 : paginationModel.page + 1,
                limit: (isNotPaid && hasDateFilter) ? 10000 : paginationModel.pageSize,
                branch_id: filters.branch_id || undefined,
                from_date: (isNotPaid && hasDateFilter) ? undefined : (filters.from_date ? formatDate(filters.from_date) : undefined),
                to_date: (isNotPaid && hasDateFilter) ? undefined : (filters.to_date ? formatDate(filters.to_date) : undefined),
                payment_status: currentStatus,
                search: search || undefined,
            };


            const res = await getPayments(params);
            const result = res?.data?.data;

            if (result && Array.isArray(result.data)) {
                let dataRows = result.data;

                if (isNotPaid && hasDateFilter) {
                    const fromDateStr = filters.from_date ? formatDate(filters.from_date) : null;
                    const toDateStr = filters.to_date ? formatDate(filters.to_date) : null;

                    dataRows = dataRows.filter(row => {
                        const orderDateStr = row.order_date ? row.order_date.split(" ")[0].split("T")[0] : null;
                        if (fromDateStr && orderDateStr && orderDateStr < fromDateStr) return false;
                        if (toDateStr && orderDateStr && orderDateStr > toDateStr) return false;
                        return true;
                    });
                }

                const normalizedRows = dataRows.map(row => ({
                    ...row,
                    customerName: row.customer?.name || "N/A",
                    branchName: row.branch?.name || "N/A",
                }));

                if (isNotPaid && hasDateFilter) {
                    const totalCount = normalizedRows.length;
                    const start = paginationModel.page * paginationModel.pageSize;
                    const end = start + paginationModel.pageSize;
                    setRows(normalizedRows.slice(start, end));
                    setRowCount(totalCount);
                } else {
                    setRows(normalizedRows);
                    setRowCount(result.total);
                }
            } else {
                setRows([]);
                setRowCount(0);
            }

        } catch (error) {
            console.error("Fetch payments error:", error);
            showSnackbar(error?.response?.data?.message || "Failed to fetch payments!", "error");
        }
        setLoading(false);
    }, [paginationModel, filters, search, showSnackbar, tabValue]);


    useEffect(() => {
        fetchBranches();
        fetchTabCounts();
    }, []);

    useEffect(() => {
        fetchPayments();
    }, [fetchPayments]);

    const handleFilterChange = (name, value) => {
        if (name === "from_date" && filters.to_date && value) {
            const from = new Date(value);
            const to = new Date(filters.to_date);
            if (from.getFullYear() === to.getFullYear() && from.getMonth() === to.getMonth() && from.getDate() === to.getDate()) {
                showSnackbar("From Date and To Date cannot be the same", "error");
                return;
            }
        }
        if (name === "to_date" && filters.from_date && value) {
            const to = new Date(value);
            const from = new Date(filters.from_date);
            if (from.getFullYear() === to.getFullYear() && from.getMonth() === to.getMonth() && from.getDate() === to.getDate()) {
                showSnackbar("From Date and To Date cannot be the same", "error");
                return;
            }
        }
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const handleApplyFilters = () => {
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
        fetchPayments();
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
    };

    const handleReset = () => {
        setFilters({
            branch_id: "",
            from_date: null,
            to_date: null,
            payment_status: tabValue === 0 ? "not_paid" : tabValue === 1 ? "advance" : "full",

        });
        setSearch("");
        setPaginationModel({ page: 0, pageSize: 10 });
    };

    const handleExport = async () => {
        try {
            const currentStatus = tabValue === 0 ? "not_paid" : tabValue === 1 ? "advance" : "full";
            const isNotPaid = tabValue === 0;
            const hasDateFilter = filters.from_date || filters.to_date;

            if (isNotPaid && hasDateFilter) {
                const params = {
                    branch_id: filters.branch_id || undefined,
                    payment_status: currentStatus,
                    search: search || undefined,
                    limit: 10000,
                };

                const res = await getPayments(params);
                const result = res?.data?.data;
                let dataRows = result && Array.isArray(result.data) ? result.data : [];

                const fromDateStr = filters.from_date ? formatDate(filters.from_date) : null;
                const toDateStr = filters.to_date ? formatDate(filters.to_date) : null;

                dataRows = dataRows.filter(row => {
                    const orderDateStr = row.order_date ? row.order_date.split(" ")[0].split("T")[0] : null;
                    if (fromDateStr && orderDateStr && orderDateStr < fromDateStr) return false;
                    if (toDateStr && orderDateStr && orderDateStr > toDateStr) return false;
                    return true;
                });

                const csvContent = "\ufeff" + generateLocalCSV(dataRows);
                const url = window.URL.createObjectURL(new Blob([csvContent], { type: "text/csv;charset=utf-8;" }));
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", `payments-${new Date().getTime()}.csv`);
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
            } else {
                const params = {
                    branch_id: filters.branch_id || undefined,
                    from_date: filters.from_date ? formatDate(filters.from_date) : undefined,
                    to_date: filters.to_date ? formatDate(filters.to_date) : undefined,
                    payment_status: currentStatus,
                    search: search || undefined,
                    export: true,
                };

                const response = await exportPayments(params);
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", `payments-${new Date().getTime()}.csv`);
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
            }
        } catch (error) {
            console.error("Export error:", error);
            showSnackbar("Failed to export payments!", "error");
        }
    };


    const columns = [
        {
            field: "order_number",
            headerName: "Order Number",
            flex: 1.2,
            minWidth: isMobile ? 120 : 150,
            renderCell: (params) => {
                let val = params.value || "";
                if (!val) return "-";

                // Remove 4-digit year segment (like -2026-) if present
                const parts = val.split("-");
                if (parts.length >= 3) {
                    const secondToLast = parts[parts.length - 2];
                    if (/^\d{4}$/.test(secondToLast)) {
                        parts.splice(parts.length - 2, 1);
                        val = parts.join("-");
                    }
                }

                if (val.length <= 4) {
                    return (
                        <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
                            <span style={{ fontWeight: 800, color: "#000000", fontSize: isMobile ? "0.75rem" : "0.90rem", whiteSpace: "nowrap" }}>{val}</span>
                        </div>
                    );
                }
                const prefix = val.slice(0, -4);
                const lastFour = val.slice(-4);
                return (
                    <div style={{ display: "flex", alignItems: "center", height: "100%", flexWrap: "nowrap" }}>
                        <span style={{ color: "#666666", fontSize: isMobile ? "0.75rem" : "0.90rem", whiteSpace: "nowrap" }}>{prefix}</span>
                        <span style={{ fontWeight: 800, color: "#000000", fontSize: isMobile ? "0.75rem" : "0.90rem", whiteSpace: "nowrap" }}>{lastFour}</span>
                    </div>
                );
            }
        },
        {
            field: "customerName",
            headerName: "Customer",
            flex: 1.2,
            minWidth: isMobile ? 130 : 150,
            renderCell: (params) => (
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', height: '100%', fontSize: isMobile ? '0.75rem' : '0.85rem' }}>
                    {params.value || "N/A"}
                </Typography>
            ),
        },
        {
            field: "branchName",
            headerName: "Branch",
            flex: 1,
            minWidth: isMobile ? 110 : 130,
            renderCell: (params) => (
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', height: '100%', fontSize: isMobile ? '0.75rem' : '0.85rem' }}>
                    {params.value || "N/A"}
                </Typography>
            ),
        },
        {
            field: "total_amount",
            headerName: "Total Amount",
            flex: 1,
            minWidth: isMobile ? 90 : 120,
            renderCell: (params) => (
                <Typography variant="body2" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', height: '100%', fontSize: isMobile ? '0.75rem' : '0.85rem' }}>
                    ₹{parseFloat(params.value).toFixed(2)}
                </Typography>
            ),
        },
        {
            field: "pending_amount",
            headerName: "Pending Amount",
            flex: 1,
            minWidth: isMobile ? 90 : 120,
            renderCell: (params) => {
                const amount = params.value;

                return (
                    <Typography
                        variant="body2"
                        fontWeight={600}
                        color="error.main"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            height: '100%',
                            fontSize: isMobile ? '0.75rem' : '0.85rem'
                        }}
                    >
                        {amount !== null && amount !== undefined
                            ? `₹${parseFloat(amount).toFixed(2)}`
                            : "-"}
                    </Typography>
                );
            },
        },
        {
            field: "derived_payment_status",
            headerName: "Status",
            flex: 0.8,
            minWidth: isMobile ? 100 : 120,
            renderCell: (params) => {
                let status = params.value?.toLowerCase() || "unpaid";
                
                const totalAmount = parseFloat(params.row.total_amount) || 0;
                const pendingAmount = parseFloat(params.row.pending_amount) || 0;

                if (status === "advance" && totalAmount === pendingAmount) {
                    status = "not paid";
                }

                const style = statusColors[status] || statusColors.unpaid;
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        <Chip
                            label={status.toUpperCase()}
                            size="small"
                            sx={{
                                backgroundColor: style.backgroundColor,
                                color: style.color,
                                fontWeight: 600,
                                fontSize: isMobile ? "0.7rem" : "0.75rem",
                                borderRadius: "8px",
                                height: '28px',
                                minWidth: '80px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                '& .MuiChip-label': {
                                    px: 1,
                                }
                            }}
                        />
                    </Box>
                );
            },
        },
        {
            field: "payment_date",
            headerName: "Payment Date & Time",
            flex: 1.3,
            minWidth: isMobile ? 140 : 185,
            renderCell: (params) => (
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', height: '100%', fontSize: isMobile ? '0.75rem' : '0.85rem' }}>
                    {formatDateTime(params.value)}
                </Typography>
            ),
        },
        {
            field: "actions",
            headerName: "Actions",
            flex: 0.9,
            minWidth: isMobile ? 80 : 120,
            sortable: false,
            renderCell: (params) => {
                const status = params.row.derived_payment_status?.toLowerCase();
                const isPaid = status === 'paid' || status === 'full';
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleOpenPayModal(params.row.id)}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: isMobile ? '0.7rem' : '0.75rem',
                                height: '28px',
                                minWidth: '80px',
                                borderRadius: '8px',
                                backgroundColor: isPaid ? '#4caf50' : Color.primary.main,
                                boxShadow: 'none',
                                '&:hover': {
                                    backgroundColor: isPaid ? '#388e3c' : Color.primary.dark,
                                    boxShadow: 'none',
                                }
                            }}
                        >
                            {isPaid ? "Details" : "Pay"}
                        </Button>
                    </Box>
                );
            }
        }
    ];


    return (
        <Box mb={{ xs: 0, sm: 1, md: 6 }} sx={{ width: "100%", padding: isMobile ? 1 : 0 }}>
            {/* Header */}
            {/* <Box mb={3} mt={0}>
                <Box>
                    <Typography variant="h5" fontWeight={700} color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Payments
                    </Typography>
                </Box>
            </Box> */}

            {/* Stats Cards */}
            <Box
                display="grid"
                gridTemplateColumns={{
                    xs: "repeat(2, 1fr)",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(4, 1fr)",
                    lg: "repeat(4, 1fr)",
                }}
                gap={2}
                sx={{ width: "100%", mt: 2, mb: 3 }}
            >
                {/* Total Orders Card */}
                <Box flex="1 1 auto" minWidth={0}>
                    <Card
                        sx={{
                            width: "100%",
                            height: { xs: 80, sm: 100, md: 100, lg: 80 },
                            borderRadius: 2,
                            boxShadow: "none",
                            backgroundColor: "white",
                            border: "1px solid #eef2f6",
                            color: "text.primary",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                        }}
                    >
                        <CardContent sx={{ p: 2, flex: 1, '&:last-child': { pb: 2 } }}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" height="100%">
                                <Box>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: { xs: "0.70rem", sm: "0.75rem", md: "0.8rem", lg: ".85rem" },
                                            lineHeight: 1.2,
                                            color: "text.secondary",
                                            fontWeight: 500,
                                        }}
                                    >
                                        Total Orders
                                    </Typography>
                                    <Typography
                                        variant="h5"
                                        fontWeight="bold"
                                        sx={{
                                            fontSize: { xs: ".80rem", sm: ".90rem", md: "1rem", lg: "1rem" },
                                            mt: { xs: 0.5, sm: 0.8 },
                                            color: "text.primary",
                                        }}
                                    >
                                        <CountUp end={parseFloat((tabCounts.not_paid || 0) + (tabCounts.advance || 0) + (tabCounts.full || 0)) || 0} duration={1.5} decimals={0} />
                                    </Typography>
                                </Box>
                                <Avatar
                                    sx={{
                                        bgcolor: "rgba(124, 11, 204, 0.1)",
                                        width: { xs: 30, sm: 34, md: 38, lg: 38 },
                                        height: { xs: 30, sm: 34, md: 38, lg: 38 },
                                    }}
                                >
                                    <ShoppingCartIcon sx={{ color: "#7C0BCC", fontSize: { xs: "1rem", sm: "1.2rem" } }} />
                                </Avatar>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>

                {/* Not Paid Card */}
                <Box flex="1 1 auto" minWidth={0}>
                    <Card
                        sx={{
                            width: "100%",
                            height: { xs: 80, sm: 100, md: 100, lg: 80 },
                            borderRadius: 2,
                            boxShadow: "none",
                            backgroundColor: "white",
                            border: "1px solid #eef2f6",
                            color: "text.primary",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                        }}
                    >
                        <CardContent sx={{ p: 2, flex: 1, '&:last-child': { pb: 2 } }}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" height="100%">
                                <Box>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: { xs: "0.70rem", sm: "0.75rem", md: "0.8rem", lg: ".85rem" },
                                            lineHeight: 1.2,
                                            color: "text.secondary",
                                            fontWeight: 500,
                                        }}
                                    >
                                        Not Paid
                                    </Typography>
                                    <Typography
                                        variant="h5"
                                        fontWeight="bold"
                                        sx={{
                                            fontSize: { xs: ".80rem", sm: ".90rem", md: "1rem", lg: "1rem" },
                                            mt: { xs: 0.5, sm: 0.8 },
                                            color: "text.primary",
                                        }}
                                    >
                                        <CountUp end={parseFloat(tabCounts.not_paid) || 0} duration={1.5} decimals={0} />
                                    </Typography>
                                </Box>
                                <Avatar
                                    sx={{
                                        bgcolor: "rgba(244, 67, 54, 0.1)",
                                        width: { xs: 30, sm: 34, md: 38, lg: 38 },
                                        height: { xs: 30, sm: 34, md: 38, lg: 38 },
                                    }}
                                >
                                    <CloseIcon sx={{ color: "#f44336", fontSize: { xs: "1rem", sm: "1.2rem" } }} />
                                </Avatar>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>

                {/* Advance Paid Card */}
                <Box flex="1 1 auto" minWidth={0}>
                    <Card
                        sx={{
                            width: "100%",
                            height: { xs: 80, sm: 100, md: 100, lg: 80 },
                            borderRadius: 2,
                            boxShadow: "none",
                            backgroundColor: "white",
                            border: "1px solid #eef2f6",
                            color: "text.primary",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                        }}
                    >
                        <CardContent sx={{ p: 2, flex: 1, '&:last-child': { pb: 2 } }}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" height="100%">
                                <Box>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: { xs: "0.70rem", sm: "0.75rem", md: "0.8rem", lg: ".85rem" },
                                            lineHeight: 1.2,
                                            color: "text.secondary",
                                            fontWeight: 500,
                                        }}
                                    >
                                        Advance Paid
                                    </Typography>
                                    <Typography
                                        variant="h5"
                                        fontWeight="bold"
                                        sx={{
                                            fontSize: { xs: ".80rem", sm: ".90rem", md: "1rem", lg: "1rem" },
                                            mt: { xs: 0.5, sm: 0.8 },
                                            color: "text.primary",
                                        }}
                                    >
                                        <CountUp end={parseFloat(tabCounts.advance) || 0} duration={1.5} decimals={0} />
                                    </Typography>
                                </Box>
                                <Avatar
                                    sx={{
                                        bgcolor: "rgba(255, 152, 0, 0.1)",
                                        width: { xs: 30, sm: 34, md: 38, lg: 38 },
                                        height: { xs: 30, sm: 34, md: 38, lg: 38 },
                                    }}
                                >
                                    <PaymentIcon sx={{ color: "#ff9800", fontSize: { xs: "1rem", sm: "1.2rem" } }} />
                                </Avatar>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>

                {/* Full Paid Card */}
                <Box flex="1 1 auto" minWidth={0}>
                    <Card
                        sx={{
                            width: "100%",
                            height: { xs: 80, sm: 100, md: 100, lg: 80 },
                            borderRadius: 2,
                            boxShadow: "none",
                            backgroundColor: "white",
                            border: "1px solid #eef2f6",
                            color: "text.primary",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                        }}
                    >
                        <CardContent sx={{ p: 2, flex: 1, '&:last-child': { pb: 2 } }}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" height="100%">
                                <Box>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: { xs: "0.70rem", sm: "0.75rem", md: "0.8rem", lg: ".85rem" },
                                            lineHeight: 1.2,
                                            color: "text.secondary",
                                            fontWeight: 500,
                                        }}
                                    >
                                        Full Paid
                                    </Typography>
                                    <Typography
                                        variant="h5"
                                        fontWeight="bold"
                                        sx={{
                                            fontSize: { xs: ".80rem", sm: ".90rem", md: "1rem", lg: "1rem" },
                                            mt: { xs: 0.5, sm: 0.8 },
                                            color: "text.primary",
                                        }}
                                    >
                                        <CountUp end={parseFloat(tabCounts.full) || 0} duration={1.5} decimals={0} />
                                    </Typography>
                                </Box>
                                <Avatar
                                    sx={{
                                        bgcolor: "rgba(76, 175, 80, 0.1)",
                                        width: { xs: 30, sm: 34, md: 38, lg: 38 },
                                        height: { xs: 30, sm: 34, md: 38, lg: 38 },
                                    }}
                                >
                                    <CheckCircleIcon sx={{ color: "#4caf50", fontSize: { xs: "1rem", sm: "1.2rem" } }} />
                                </Avatar>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </Box>

            {/* Search + Filters (Reports Style) */}
            <Box
                sx={{
                    mt: 2,
                    mb: 3,
                    p: 2,
                    border: "1px solid lightgray",
                    borderRadius: 2,
                    backgroundColor: "#fafafa",
                }}
            >
                <Grid container spacing={isMobile ? 2 : 3} alignItems="center" justifyContent="space-between">
                    <Grid item xs={12}>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center", width: "100%", justifyContent: isMobile ? 'center' : 'flex-start' }}>

                            <Tabs
                                value={tabValue}
                                onChange={handleTabChange}
                                variant={isMobile ? "scrollable" : "standard"}
                                scrollButtons={false}
                                sx={{
                                    width: { xs: '100%', sm: 'auto' },
                                    minHeight: 'auto',
                                    py: 0,
                                    '& .MuiTabs-scroller': {
                                        overflow: 'visible !important',
                                    },
                                    '& .MuiTabs-indicator': {
                                        display: 'none',
                                    },
                                    '& .MuiTabs-flexContainer': {
                                        justifyContent: { xs: 'center', md: 'flex-start' },
                                        width: '100%',
                                    },

                                    '& .MuiTab-root': {
                                        flex: { xs: 1, sm: 'initial' },
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        fontSize: isMobile ? '0.75rem' : '0.9rem',
                                        minWidth: isMobile ? 'auto' : 160,
                                        borderRadius: '8px',
                                        minHeight: '37px',
                                        height: '37px',
                                        padding: isMobile ? '4px 10px' : '0px 20px',
                                        marginRight: { xs: '4px', sm: '12px' },
                                        '&:last-of-type': {
                                            marginRight: 0,
                                        },
                                        backgroundColor: '#ffffff',
                                        color: '#666',
                                        border: '1px solid #e0e0e0',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                                        flexShrink: 0,
                                        '&:hover': {
                                            backgroundColor: '#f8f9fa',
                                            borderColor: Color.secondary.main,
                                        },
                                    },
                                    '& .Mui-selected': {
                                        backgroundColor: `${Color.secondary.main} !important`,
                                        color: '#ffffff !important',
                                        borderColor: Color.secondary.main,
                                        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                                    }
                                }}
                            >
                                <Tab label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <span>Not Paid</span>
                                        {tabCounts.not_paid > 0 && (
                                            <Box sx={{
                                                width: 20,
                                                height: 20,
                                                borderRadius: '50%',
                                                backgroundColor: tabValue === 0 ? '#fff' : Color.primary.main,
                                                color: tabValue === 0 ? Color.secondary.main : '#fff',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '0.7rem',
                                                fontWeight: 700,
                                                flexShrink: 0
                                            }}>
                                                {tabCounts.not_paid}
                                            </Box>
                                        )}
                                    </Box>
                                } />
                                 <Tab label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <span>Advance Paid</span>
                                        {tabCounts.advance > 0 && (
                                            <Box sx={{
                                                width: 20,
                                                height: 20,
                                                borderRadius: '50%',
                                                backgroundColor: tabValue === 1 ? '#fff' : Color.primary.main,
                                                color: tabValue === 1 ? Color.secondary.main : '#fff',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '0.7rem',
                                                fontWeight: 700,
                                                flexShrink: 0
                                            }}>
                                                {tabCounts.advance}
                                            </Box>
                                        )}
                                    </Box>
                                } />
                                <Tab label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <span>Full Paid</span>
                                        {tabCounts.full > 0 && (
                                            <Box sx={{
                                                width: 20,
                                                height: 20,
                                                borderRadius: '50%',
                                                backgroundColor: tabValue === 2 ? '#fff' : Color.primary.main,
                                                color: tabValue === 2 ? Color.secondary.main : '#fff',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '0.7rem',
                                                fontWeight: 700,
                                                flexShrink: 0
                                            }}>
                                                {tabCounts.full}
                                            </Box>
                                        )}
                                    </Box>
                                } />
                            </Tabs>

                            <TextField
                                placeholder="Search by customer, order no..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === "Enter") {
                                        handleApplyFilters();
                                    }
                                }}
                                size="small"
                                sx={{
                                    width: { xs: "100%", sm: 250 },
                                    backgroundColor: "#ffffff",
                                    "& .MuiOutlinedInput-root": {
                                        height: 37,
                                        fontSize: "0.85rem"
                                    }
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search fontSize="small" />
                                        </InputAdornment>
                                    ),
                                    endAdornment: search ? (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => { setSearch(""); handleApplyFilters(); }}>
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null
                                }}
                            />

                            {/* Branch Select */}
                            {!isManager && (
                                <FormControl size="small" sx={{ width: { xs: "100%", sm: 220 } }}>
                                    <Select
                                        value={filters.branch_id}
                                        onChange={(e) => handleFilterChange("branch_id", e.target.value)}
                                        displayEmpty
                                        sx={{
                                            height: 37,
                                            fontSize: "0.9rem",
                                            backgroundColor: '#ffffff',
                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' }
                                        }}
                                    >
                                        <MenuItem value="">All Branches</MenuItem>
                                        {Array.isArray(branches) && branches.map((branch) => (
                                            <MenuItem key={branch.id} value={branch.id}>
                                                {branch.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}

                            {/* Date Picker Group */}
                            <Box sx={{
                                display: 'flex',
                                gap: 1,
                                width: { xs: '100%', sm: 'auto' },
                                justifyContent: 'space-between'
                            }}>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                        value={filters.from_date}
                                        onChange={(val) => handleFilterChange("from_date", val)}
                                        format="dd/MM/yyyy"
                                        maxDate={filters.to_date ? subDays(new Date(filters.to_date), 1) : undefined}
                                        slotProps={{
                                            textField: {
                                                size: "small",
                                                placeholder: "From Date",
                                                sx: {
                                                    width: { xs: '48.5%', sm: 150 },
                                                    '& .MuiOutlinedInput-root': { height: 37, fontSize: '0.85rem', backgroundColor: '#ffffff' }
                                                }
                                            }
                                        }}
                                    />
                                    <DatePicker
                                        value={filters.to_date}
                                        onChange={(val) => handleFilterChange("to_date", val)}
                                        format="dd/MM/yyyy"
                                        minDate={filters.from_date ? addDays(new Date(filters.from_date), 1) : undefined}
                                        slotProps={{
                                            textField: {
                                                size: "small",
                                                placeholder: "To Date",
                                                sx: {
                                                    width: { xs: '48.5%', sm: 150 },
                                                    '& .MuiOutlinedInput-root': { height: 37, fontSize: '0.85rem', backgroundColor: '#ffffff' }
                                                }
                                            }
                                        }}
                                    />
                                </LocalizationProvider>
                            </Box>

                            {/* Reset & Refresh Buttons */}
                            <Button
                                onClick={handleReset}
                                size="small"
                                sx={{
                                    height: 37,
                                    width: { xs: "100%", sm: "auto" },
                                    border: `1px solid ${Color.secondary.main}`,
                                    color: Color.secondary.main,
                                    fontSize: "0.85rem",
                                    fontWeight: 600,
                                    px: 2.5,
                                    textTransform: 'none',
                                    borderRadius: '8px',
                                    '&:hover': { backgroundColor: 'rgba(233, 30, 99, 0.04)', borderColor: Color.secondary.main }
                                }}
                            >
                                Reset
                            </Button>

                            <Button
                                variant="contained"
                                onClick={handleExport}
                                startIcon={<DownloadIcon />}
                                sx={{
                                    height: 37,
                                    width: { xs: "100%", sm: "auto" },
                                    backgroundColor: Color.secondary.main,
                                    color: "white",
                                    fontSize: "0.85rem",
                                    fontWeight: 600,
                                    px: 2.5,
                                    textTransform: 'none',
                                    borderRadius: '8px',
                                    '&:hover': {
                                        backgroundColor: Color.secondary.main,
                                        opacity: 0.9
                                    }
                                }}
                            >
                                Export
                            </Button>

                            {/* <Button
                                onClick={fetchPayments}
                                size="small"
                                sx={{
                                    height: 37,
                                    width: { xs: "100%", sm: "auto" },
                                    background: Color.primary.main,
                                    color: "white",
                                    fontSize: "0.85rem",
                                    fontWeight: 600,
                                    px: 2.5,
                                    textTransform: 'none',
                                    borderRadius: '8px',
                                    '&:hover': { background: Color.primary.dark }
                                }}
                            >
                                Refresh
                            </Button> */}
                        </Box>
                    </Grid>
                </Grid>
            </Box>


            {/* Table */}
            <Box sx={{
                width: "100%",
                backgroundColor: "white",
                borderRadius: 3,
                overflow: "hidden",
                boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                border: "1px solid #f0f0f0"
            }}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    rowCount={rowCount}
                    paginationModel={paginationModel}
                    paginationMode="server"
                    onPaginationModelChange={setPaginationModel}
                    disableRowSelectionOnClick
                    pageSizeOptions={[10, 25, 50]}
                    rowHeight={isMobile ? 50 : 60}
                    disableColumnMenu
                    disableColumnSorting
                    sx={{
                        fontFamily: "'Poppins', sans-serif",
                        border: "none",
                        "& .MuiDataGrid-columnHeaders": {
                            backgroundColor: "#f8f9fa",
                            color: "#333",
                            fontWeight: 700,
                            fontSize: isMobile ? "0.8rem" : "0.9rem",
                            borderBottom: "1px solid #eee",
                        },
                        "& .MuiDataGrid-cell": {
                            borderBottom: "1px solid #f5f5f5",
                            fontSize: isMobile ? "0.75rem" : "0.85rem",
                            display: 'flex',
                            alignItems: 'center',
                        },
                        "& .MuiDataGrid-row:nth-of-type(even)": { backgroundColor: "#fff" },
                        "& .MuiDataGrid-row:nth-of-type(odd)": { backgroundColor: "#f9f9f9" },
                        "& .MuiDataGrid-footerContainer": {
                            borderTop: "1px solid #eee",
                        },
                        "& .MuiDataGrid-row:hover": {
                            backgroundColor: "rgba(124, 11, 204, 0.02) !important",
                        },
                        "& .MuiLinearProgress-bar": { backgroundColor: Color.primary.main },
                    }}
                />
            </Box>

            {/* Pay Modal */}
            <Dialog
                open={payModalOpen}
                onClose={() => !submittingPayment && setPayModalOpen(false)}
                maxWidth="lg"
                fullWidth
                sx={{
                    '& .MuiDialog-paper': {
                        borderRadius: '16px',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
                        overflow: 'hidden'
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        m: 0,
                        p: 1.5,
                        backgroundColor: '#f8f9fa',
                        borderBottom: '1px solid #eef2f6',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PaymentIcon sx={{ color: Color.primary.main, fontSize: '1.2rem' }} />
                        <Typography variant="h6" fontWeight={700} sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '1rem', color: 'text.primary' }}>
                            Payment Details
                        </Typography>
                    </Box>
                    <IconButton
                        aria-label="close"
                        onClick={() => setPayModalOpen(false)}
                        disabled={submittingPayment}
                        sx={{
                            color: (theme) => theme.palette.grey[500],
                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers sx={{ p: 1.5, backgroundColor: '#fafafa' }}>
                    {paymentLoading ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6, gap: 1.5 }}>
                            <CircularProgress size={30} sx={{ color: Color.primary.main }} />
                            <Typography variant="caption" color="text.secondary">Fetching order details...</Typography>
                        </Box>
                    ) : orderData ? (
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 1.5, width: '100%' }}>
                            {/* Left Side - 30% Width */}
                            <Box sx={{ width: { xs: '100%', md: '35%' }, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {/* Invoice & Branch Info Card */}
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 1.5,
                                        borderRadius: '8px',
                                        border: '1px solid #eef2f6',
                                        background: 'linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1.2,
                                        width: '100%'
                                    }}
                                >
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ letterSpacing: '0.5px', fontSize: '0.625rem', textTransform: 'uppercase' }}>
                                                INVOICE NUMBER
                                            </Typography>
                                            <Typography fontWeight={600} color={Color.primary.main} sx={{ mt: 0.2, fontSize: '0.7rem' }}>
                                                {orderData.order_number}
                                            </Typography>
                                        </Box>
                                        <Divider orientation="vertical" flexItem sx={{ mx: 1.5, borderColor: '#ccc' }} />
                                        <Box sx={{ flex: 1, textAlign: 'right' }}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ letterSpacing: '0.5px', fontSize: '0.625rem', textTransform: 'uppercase' }}>
                                                BRANCH
                                            </Typography>
                                            <Typography fontWeight={600} color="text.primary" sx={{ mt: 0.2, fontSize: '0.7rem' }}>
                                                {orderData.branch?.name || orderData.branch_code || "Main Branch"}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Divider sx={{ borderColor: 'rgba(0,0,0,0.06)' }} />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ letterSpacing: '0.5px', fontSize: '0.625rem', textTransform: 'uppercase' }}>
                                                ORDER STATUS
                                            </Typography>
                                            <Typography fontWeight={600} sx={{
                                                mt: 0.2,
                                                fontSize: '0.7rem',
                                                color: orderData.status === "cancel_request_rejected" ? "error.main" : Color.primary.main,
                                                textTransform: 'uppercase'
                                            }}>
                                                {orderData.status ? orderData.status.replace(/_/g, ' ') : "N/A"}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Paper>

                                {/* Products details (Read-Only) */}
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 1.5,
                                        borderRadius: '8px',
                                        border: '1px solid #eef2f6',
                                        backgroundColor: '#fff',
                                        width: '100%'
                                    }}
                                >
                                    <Typography variant="subtitle2" fontWeight={800} color="text.primary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        Products Table
                                    </Typography>
                                    <TableContainer sx={{ maxHeight: 280, overflowY: 'auto', borderRadius: '4px', border: '1px solid #f0f4f8' }}>
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 800, backgroundColor: '#f8f9fa', fontSize: '0.7rem', py: 1 }}>Product</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 800, backgroundColor: '#f8f9fa', fontSize: '0.7rem', py: 1 }}>Price</TableCell>
                                                    <TableCell align="center" sx={{ fontWeight: 800, backgroundColor: '#f8f9fa', fontSize: '0.7rem', py: 1 }}>Qty</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 800, backgroundColor: '#f8f9fa', fontSize: '0.7rem', py: 1 }}>Total</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {(orderData.items || []).map((item, index) => {
                                                    const price = parseFloat(item.price || item.product?.base_price || 0);
                                                    const qty = parseFloat(item.quantity || 1);
                                                    const itemTotal = price * qty;
                                                    return (
                                                        <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { backgroundColor: '#fafafa' } }}>
                                                            <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600, py: 0.8 }}>{item.product?.name || "N/A"}</TableCell>
                                                            <TableCell align="right" sx={{ fontSize: '0.7rem', py: 0.8 }}>₹{price.toFixed(2)}</TableCell>
                                                            <TableCell align="center" sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary', py: 0.8 }}>{qty}</TableCell>
                                                            <TableCell align="right" sx={{ fontSize: '0.7rem', py: 0.8, fontWeight: 700, color: Color.primary.main }}>₹{itemTotal.toFixed(2)}</TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Paper>
                            </Box>

                            {/* Right Side - 70% Width */}
                            <Box sx={{ width: { xs: '100%', md: '65%' }, display: 'flex', flexDirection: 'column', gap: 1.5 }}>

                                {/* Payments Table */}
                                <Paper elevation={0} sx={{ p: 1.5, borderRadius: '8px', border: '1px solid #eef2f6', backgroundColor: '#fff', width: '100%' }}>
                                    <Typography variant="subtitle2" fontWeight={800} color="secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        Payment History
                                    </Typography>

                                    <Box sx={{ overflowX: 'auto', borderRadius: '6px', border: "1px solid #eef2f6", backgroundColor: '#fff', mb: 1.5 }}>
                                        <Table size="small" sx={{ minWidth: 400 }}>
                                            {modalPayments.length > 0 && (
                                                <TableHead>
                                                    <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                                                        <TableCell sx={{ fontWeight: 700, color: "text.secondary", textTransform: 'uppercase', fontSize: '9px', letterSpacing: '0.5px', py: 1 }}>Method</TableCell>
                                                        <TableCell align="center" sx={{ fontWeight: 700, color: "text.secondary", textTransform: 'uppercase', fontSize: '9px', letterSpacing: '0.5px', py: 1 }}>Amount</TableCell>
                                                        <TableCell align="center" sx={{ fontWeight: 700, color: "text.secondary", textTransform: 'uppercase', fontSize: '9px', letterSpacing: '0.5px', py: 1 }}>Cashier</TableCell>
                                                        <TableCell align="center" sx={{ fontWeight: 700, color: "text.secondary", textTransform: 'uppercase', fontSize: '9px', letterSpacing: '0.5px', py: 1 }}>Date & Time</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 700, color: "text.secondary", textTransform: 'uppercase', fontSize: '9px', letterSpacing: '0.5px', py: 1 }}>Actions</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                            )}
                                            <TableBody>
                                                {modalPayments.map((payment, index) => (
                                                    <TableRow key={index} sx={{ "&:hover": { backgroundColor: "#f8faff" } }}>
                                                        <TableCell sx={{ py: 0.8 }}>
                                                            <Typography sx={{ fontWeight: 600, px: 1, py: 0.2, borderRadius: 4, bgcolor: '#f0f4f8', color: '#1a365d', display: 'inline-block', fontSize: '0.7rem' }}>
                                                                {paymentMethods.find((s) => s.value === payment.payment_method)?.label || "-"}
                                                            </Typography>
                                                            {payment.status === 'pending_approval' && (
                                                                <Chip label="Pending Approval" size="small" sx={{ ml: 0.5, height: 18, fontSize: '0.6rem', bgcolor: '#fff7ed', color: '#c2410c', fontWeight: 600, border: '1px solid #ffedd5' }} />
                                                            )}
                                                            {payment.status === 'rejected' && (
                                                                <Chip label="Rejected" size="small" sx={{ ml: 0.5, height: 18, fontSize: '0.6rem', bgcolor: '#fef2f2', color: '#dc2626', fontWeight: 600, border: '1px solid #fee2e2' }} />
                                                            )}
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ py: 0.8 }}>
                                                            <Typography sx={{ fontWeight: 700, color: Color.primary.main, fontSize: '0.75rem' }}>
                                                                ₹{parseFloat(payment.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ py: 0.8 }}>
                                                            <Typography sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.7rem' }}>
                                                                {payment.cashier_name || "-"}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ py: 0.8 }}>
                                                            <Typography sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.725rem' }}>
                                                                {formatDateTime(payment.payment_date)}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ py: 0.8 }}>
                                                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                                                                {tabValue !== 2 && (
                                                                    <Tooltip title="Edit Payment">
                                                                        <IconButton
                                                                            onClick={() => handleEditPayment(index)}
                                                                            disabled={orderData.status === "cancel_request_rejected"}
                                                                            size="small"
                                                                            sx={{
                                                                                color: "text.secondary",
                                                                                bgcolor: '#f5f5f5',
                                                                                p: 0.4,
                                                                                '&:hover': { bgcolor: '#f5f5f5', color: 'text.secondary' },
                                                                                '&:disabled': { opacity: 0.5, cursor: 'not-allowed' }
                                                                            }}
                                                                        >
                                                                            <EditIcon sx={{ fontSize: 13 }} />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                )}
                                                                {tabValue !== 2 && (
                                                                    <Tooltip title="Remove Payment">
                                                                        <IconButton
                                                                            onClick={() => handleRemovePayment(index)}
                                                                            disabled={orderData.status === "cancel_request_rejected"}
                                                                            size="small"
                                                                            sx={{
                                                                                color: "#ef5350",
                                                                                bgcolor: '#fff0f0',
                                                                                p: 0.4,
                                                                                '&:hover': { bgcolor: '#fff0f0', color: '#ef5350' },
                                                                                '&:disabled': { opacity: 0.5, cursor: 'not-allowed' }
                                                                            }}
                                                                        >
                                                                            <DeleteIcon sx={{ fontSize: 13 }} />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                )}
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                                {modalPayments.length === 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={5} align="center" sx={{ py: 2.5, backgroundColor: '#fafbfd' }}>
                                                            <Typography sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.725rem', fontStyle: 'italic' }}>
                                                                No payments recorded yet.
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </Box>

                                    {/* Add Payment Trigger */}
                                    <Button
                                        onClick={() => {
                                            setShowAddPaymentFields(true);
                                            setEditingPaymentIndex(null);
                                            setNewPayment({ amount: "", payment_method: "", payment_date: new Date() });
                                        }}
                                        startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                                        disabled={showAddPaymentFields || getModalTotals().pending <= 0 || orderData.status === "cancel_request_rejected"}
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                            borderRadius: 1.5,
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            px: 2,
                                            py: 0.4,
                                            fontSize: '0.7rem',
                                            color: Color.primary.main,
                                            borderColor: Color.primary.light,
                                            "&:hover": {
                                                borderColor: Color.primary.main,
                                                backgroundColor: 'rgba(216, 27, 96, 0.04)',
                                            },
                                            "&:disabled": {
                                                color: showAddPaymentFields ? "rgba(216, 27, 96, 0.6)" : "#7e7a7aff",
                                                borderColor: showAddPaymentFields ? "rgba(216, 27, 96, 0.3)" : "#7e7a7aff",
                                                cursor: "not-allowed",
                                            },
                                        }}
                                    >
                                        Add Payment
                                    </Button>

                                    {/* Add / Edit Form Card */}
                                    {showAddPaymentFields && (
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 1.2,
                                                mt: 1.2,
                                                borderRadius: '6px',
                                                border: "1px solid",
                                                borderColor: editingPaymentIndex !== null ? Color.primary.light : "#eef2f6",
                                                backgroundColor: editingPaymentIndex !== null ? "#f8fbff" : "#fdfdfe",
                                            }}
                                        >
                                            <Typography variant="caption" sx={{ mb: 1, fontWeight: 700, color: Color.primary.main, display: 'block' }}>
                                                {editingPaymentIndex !== null ? "Edit Payment Details" : "Record New Payment"}
                                            </Typography>

                                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'flex-start' }, gap: 1.5, flexWrap: 'wrap', width: '100%' }}>
                                                <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: '150px' } }}>
                                                    <Typography sx={{ mb: 0.3, fontWeight: 600, color: Color.label?.main || '#666', fontSize: "10px" }}>
                                                        Payment Method*
                                                    </Typography>
                                                    <FormControl fullWidth size="small" error={!!paymentErrors.payment_method}>
                                                        <Select
                                                            value={newPayment.payment_method}
                                                            onChange={(e) => {
                                                                setNewPayment(prev => ({ ...prev, payment_method: e.target.value }));
                                                                setPaymentErrors(prev => ({ ...prev, payment_method: "" }));
                                                            }}
                                                            displayEmpty
                                                            sx={{ height: 32, fontSize: '0.75rem' }}
                                                        >
                                                            <MenuItem value="" disabled sx={{ fontSize: '0.75rem' }}>Select Method</MenuItem>
                                                            {paymentMethods.map((s) => (
                                                                <MenuItem key={s.value} value={s.value} sx={{ fontSize: '0.75rem' }}>{s.label}</MenuItem>
                                                            ))}
                                                        </Select>
                                                        {paymentErrors.payment_method && (
                                                            <FormHelperText sx={{ fontSize: '9px' }}>{paymentErrors.payment_method}</FormHelperText>
                                                        )}
                                                    </FormControl>
                                                </Box>

                                                <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: '120px' } }}>
                                                    <Typography sx={{ mb: 0.3, fontWeight: 600, color: Color.label?.main || '#666', fontSize: "10px" }}>
                                                        Amount*
                                                    </Typography>
                                                    <TextField
                                                        type="number"
                                                        value={newPayment.amount}
                                                        onChange={(e) => {
                                                            setNewPayment(prev => ({ ...prev, amount: e.target.value }));
                                                            setPaymentErrors(prev => ({ ...prev, amount: "" }));
                                                        }}
                                                        placeholder="Enter Amount"
                                                        fullWidth
                                                        size="small"
                                                        error={!!paymentErrors.amount}
                                                        helperText={paymentErrors.amount}
                                                        FormHelperTextProps={{ sx: { fontSize: '9px' } }}
                                                        InputProps={{
                                                            startAdornment: <InputAdornment position="start" sx={{ '& .MuiTypography-root': { fontSize: '0.75rem' } }}>₹</InputAdornment>,
                                                            sx: { fontSize: '0.75rem' }
                                                        }}
                                                        sx={{ '& .MuiOutlinedInput-root': { height: 32 } }}
                                                    />
                                                </Box>

                                                <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: '150px' } }}>
                                                    <Typography sx={{ mb: 0.3, fontWeight: 600, color: Color.label?.main || '#666', fontSize: "10px" }}>
                                                        Cashier Name*
                                                    </Typography>
                                                    <TextField
                                                        value={newPayment.cashier_name || ""}
                                                        onChange={(e) => {
                                                            const onlyText = e.target.value.replace(/[^a-zA-Z\s]/g, "");
                                                            setNewPayment(prev => ({ ...prev, cashier_name: onlyText }));
                                                            setPaymentErrors(prev => ({ ...prev, cashier_name: "" }));
                                                        }}
                                                        placeholder="Cashier Name"
                                                        fullWidth
                                                        size="small"
                                                        error={!!paymentErrors.cashier_name}
                                                        helperText={paymentErrors.cashier_name}
                                                        FormHelperTextProps={{ sx: { fontSize: '9px' } }}
                                                        InputProps={{
                                                            sx: { fontSize: '0.75rem' }
                                                        }}
                                                        sx={{ '& .MuiOutlinedInput-root': { height: 32 } }}
                                                    />
                                                </Box>

                                                <Box sx={{ flex: 1.2, minWidth: { xs: '100%', sm: '180px' } }}>
                                                    <Typography sx={{ mb: 0.3, fontWeight: 600, color: Color.label?.main || '#666', fontSize: "10px" }}>
                                                        Date & Time
                                                    </Typography>
                                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                        <DateTimePicker
                                                            value={newPayment.payment_date}
                                                            onChange={(val) => setNewPayment(prev => ({ ...prev, payment_date: val }))}
                                                            maxDateTime={new Date()}
                                                            slotProps={{
                                                                textField: {
                                                                    size: "small",
                                                                    fullWidth: true,
                                                                    InputProps: { sx: { fontSize: '0.75rem' } },
                                                                    sx: { '& .MuiOutlinedInput-root': { height: 32 } }
                                                                }
                                                            }}
                                                        />
                                                    </LocalizationProvider>
                                                </Box>

                                                <Box sx={{ display: 'flex', gap: 1, height: 32, alignItems: 'center', mt: { xs: 0, sm: 2 } }}>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={handleCancelEditPayment}
                                                        sx={{ borderRadius: 1, px: 2, fontSize: '0.7rem', color: 'text.secondary', borderColor: '#d1d5db', height: '100%', textTransform: 'none' }}
                                                    >
                                                        Cancel
                                                    </Button>
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        onClick={editingPaymentIndex !== null ? handleSaveEditedPayment : handleAddPayment}
                                                        sx={{
                                                            borderRadius: 1, bgcolor: Color.primary.main, height: '100%', textTransform: 'none', fontSize: '0.7rem', fontWeight: 600,
                                                            '&:hover': { bgcolor: Color.primary.dark }
                                                        }}
                                                    >
                                                        {editingPaymentIndex !== null ? "Save Changes" : "Record"}
                                                    </Button>
                                                </Box>
                                            </Box>
                                        </Paper>
                                    )}
                                </Paper>

                                {/* Totals Summary & Submit Card */}
                                <Paper elevation={0} sx={{ p: 1.5, borderRadius: '8px', border: '1px solid #eef2f6', width: '100%' }}>
                                    {orderData.status === "cancel_request_rejected" && (
                                        <Alert severity="warning" sx={{ mb: 1.5, py: 0.2, px: 1, fontSize: '0.725rem', borderRadius: '6px' }}>
                                            Payments are locked because this order's cancel request was rejected.
                                        </Alert>
                                    )}

                                    <Typography variant="subtitle2" fontWeight={800} color="primary" sx={{ mb: 1 }}>
                                        Financial Summary
                                    </Typography>

                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Packing Charges:</Typography>
                                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 500 }}>₹{(parseFloat(orderData.packing_charges) || 0).toFixed(2)}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Utensils Charges:</Typography>
                                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 500 }}>₹{(parseFloat(orderData.utensils_charge) || 0).toFixed(2)}</Typography>
                                        </Box>
                                        {parseFloat(orderData.discount_amount || 0) > 0 && (
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Discount ({orderData.discount_type}):</Typography>
                                                <Typography sx={{ fontSize: '0.7rem', fontWeight: 500, color: "error.main" }}>-₹{(parseFloat(orderData.discount_amount) || 0).toFixed(2)}</Typography>
                                            </Box>
                                        )}
                                        {parseFloat(orderData.tax_amount || 0) > 0 && (() => {
                                            const quotationTaxPct = parseFloat(orderData.quotation_tax_percentage || 0);
                                            const halfTax = (parseFloat(orderData.tax_amount) / 2).toFixed(2);
                                            let rateLabel = '';
                                            if (quotationTaxPct > 0) {
                                                rateLabel = ` (${(quotationTaxPct / 2).toFixed(1)}%)`;
                                            } else if (Array.isArray(orderData.items) && orderData.items.length > 0) {
                                                const nonZeroRates = [...new Set(
                                                    orderData.items
                                                        .map(i => parseFloat(i.tax_rate ?? i.product?.gst_percentage ?? i.product?.tax_rate ?? 0))
                                                        .filter(r => r > 0)
                                                )];
                                                if (nonZeroRates.length === 1) {
                                                    rateLabel = ` (${(nonZeroRates[0] / 2).toFixed(1)}%)`;
                                                }
                                            }
                                            return (
                                                <>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>CGST{rateLabel}:</Typography>
                                                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 500 }}>₹{halfTax}</Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>SGST{rateLabel}:</Typography>
                                                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 500 }}>₹{halfTax}</Typography>
                                                    </Box>
                                                </>
                                            );
                                        })()}
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.3, pt: 0.3, borderTop: '1px dotted #ccc' }}>
                                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Total Amount:</Typography>
                                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: "primary" }}>₹{getModalTotals().total.toFixed(2)}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: "success.main" }}>Approved Paid Amount:</Typography>
                                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: "success.main" }}>₹{getModalTotals().paid.toFixed(2)}</Typography>
                                        </Box>
                                        {getModalTotals().pendingApproval > 0 && (
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: "#e65100" }}>Pending Admin Approval:</Typography>
                                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: "#e65100" }}>₹{getModalTotals().pendingApproval.toFixed(2)}</Typography>
                                            </Box>
                                        )}
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: "#d81b60" }}>Remaining Unpaid Balance:</Typography>
                                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: "#d81b60" }}>₹{getModalTotals().remainingToCollect.toFixed(2)}</Typography>
                                        </Box>
                                    </Box>

                                    {tabValue !== 2 && (
                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            onClick={handleSubmitPayment}
                                            disabled={submittingPayment || orderData.status === "cancel_request_rejected"}
                                            sx={{
                                                mt: 1.5,
                                                py: 0.7,
                                                fullWidth: true,
                                                width: '100%',
                                                textTransform: 'none',
                                                fontWeight: 700,
                                                fontSize: '0.75rem',
                                                borderRadius: '6px',
                                                boxShadow: 'none',
                                                '&:hover': { boxShadow: 'none' }
                                            }}
                                        >
                                            {submittingPayment ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : "Submit & Save Payment Changes"}
                                        </Button>
                                    )}
                                </Paper>
                            </Box>
                        </Box>
                    ) : (
                        <Alert severity="error" sx={{ borderRadius: 2 }}>
                            Failed to load order details. Please try again.
                        </Alert>
                    )}
                </DialogContent>

                {/* <DialogActions sx={{ p: 2, backgroundColor: '#f8f9fa', borderTop: '1px solid #eef2f6' }}>
                    <Button
                        onClick={() => setPayModalOpen(false)}
                        disabled={submittingPayment}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            color: 'text.secondary',
                            borderRadius: '8px',
                            px: 3
                        }}
                    >
                        Close
                    </Button>
                </DialogActions> */}
            </Dialog>
        </Box>
    );
};

export default Payments;
